import { authHeaders } from "./api-client";

const SESSION_KEY = "cf-analytics-sid";
const FLUSH_INTERVAL_MS = 5_000;
const MAX_QUEUE = 50;

type AnalyticsPayload = {
  sessionId: string;
  event: "pageview" | "click";
  pathname: string;
  referrer: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  targetText: string;
  targetHref: string;
  screenWidth: number;
};

const queue: AnalyticsPayload[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let installed = false;

function getSessionId(): string {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get("utm_source") ?? "",
    utmMedium: params.get("utm_medium") ?? "",
    utmCampaign: params.get("utm_campaign") ?? "",
    utmContent: params.get("utm_content") ?? "",
  };
}

function enqueue(payload: AnalyticsPayload) {
  queue.push(payload);
  if (queue.length >= MAX_QUEUE) {
    void flush();
  }
}

async function flush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, MAX_QUEUE);
  try {
    await fetch("/api/analytics/events", {
      method: "POST",
      // Include the session token when present so the server can flag the
      // visit as authenticated. Only a boolean is stored — no user identity.
      headers: authHeaders(),
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
  } catch {
    // Re-queue on failure (drop if too large to avoid infinite growth)
    if (queue.length < MAX_QUEUE * 2) {
      queue.unshift(...batch);
    }
  }
}

export function trackPageview(pathname?: string) {
  const utm = getUtmParams();
  enqueue({
    sessionId: getSessionId(),
    event: "pageview",
    pathname: pathname ?? window.location.pathname,
    referrer: document.referrer,
    ...utm,
    targetText: "",
    targetHref: "",
    screenWidth: window.innerWidth,
  });
}

export function trackClick(targetText: string, targetHref: string) {
  const utm = getUtmParams();
  enqueue({
    sessionId: getSessionId(),
    event: "click",
    pathname: window.location.pathname,
    referrer: "",
    ...utm,
    targetText: targetText.slice(0, 200),
    targetHref: targetHref.slice(0, 500),
    screenWidth: window.innerWidth,
  });
}

function handleClickEvent(e: MouseEvent) {
  const target = e.target as HTMLElement | null;
  if (!target) return;

  // Walk up to find closest <a> or <button>
  const el = target.closest("a, button, [role='button'], [data-track]");
  if (!el) return;

  const text = (el as HTMLElement).dataset.trackLabel ?? el.textContent?.trim().slice(0, 200) ?? "";
  const href = (el as HTMLAnchorElement).href ?? (el as HTMLElement).dataset.trackHref ?? "";

  if (text || href) {
    trackClick(text, href);
  }
}

export function installAnalytics() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Track initial pageview
  trackPageview();

  // Listen for clicks
  document.addEventListener("click", handleClickEvent, { capture: true, passive: true });

  // Flush on interval
  flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);

  // Flush on page unload
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flush();
    }
  });

  window.addEventListener("pagehide", () => void flush());
}

export function uninstallAnalytics() {
  if (!installed) return;
  installed = false;
  document.removeEventListener("click", handleClickEvent, { capture: true });
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  void flush();
}
